import { returnAllTradeDetailsFromGenericParams } from "@/lib/agGridColDefs/colDefGlobalHelpers";
import tradeDataStore from "@/store/tradeDataStore";
import {
  getLegSummaryDataFromStore,
  formatPriceWithNotation,
  formatPriceForDisplay,
} from "./fillsSummaryHelpers";
import {
  containsHyphenInQuote,
  convertToDollarFraction,
  formatDollarFractionString,
  formatDollarFractionZValueForSmallFractionDisplay,
  returnFractionString,
  ruleOfEights,
} from "@/lib/businessRules/dollarFractionBusinessRule";
import { ReactNode } from "react";

/**
 * FillsSummaryPriceCellRenderer
 *
 * Displays the fill price at the leg level.
 * - Main display: Shows block value from API (current server state)
 * - Tooltip: Handled by tooltipValueGetter on the column definition
 */
const FillsSummaryPriceCellRenderer = (params: any) => {
  // Subscribe to Zustand store for reactive updates (hooks must run before any early return)
  tradeDataStore((state) => state.tradeData);

  if (!params.data) {
    return null;
  }

  const { fullTradeData } = returnAllTradeDetailsFromGenericParams(params);
  const esid = fullTradeData?.esid;
  const blockId = params.data?.blockId;

  let fillPrice: number | string | null = null;
  let notation = undefined;

  if (esid && blockId !== undefined) {
    const legData = getLegSummaryDataFromStore(esid, blockId, params.data);
    fillPrice = legData.fillPrice;
    notation = legData.notation;
  }

  // Handle $fr notation: convert Z-value digit to superscript fraction for display (no $fr suffix)
  let displayValue: ReactNode = "";
  if (notation === "$fr" && fillPrice !== null) {
    // Resolve the fraction string: if numeric, convert to fraction first
    let fractionInput: string | null = null;
    if (typeof fillPrice === "string" && containsHyphenInQuote(fillPrice)) {
      fractionInput = fillPrice;
    } else if (typeof fillPrice === "number") {
      const converted = convertToDollarFraction(fillPrice);
      if (converted.includes("-")) {
        fractionInput = converted;
      }
    }

    if (fractionInput) {
      const fractionStr = formatDollarFractionString(fractionInput);
      const valueAfterHyphen = returnFractionString(fractionInput, false);

      if (valueAfterHyphen.length === 3) {
        const quoteDisplayValue = fractionStr.slice(0, -1);
        const zValueDecimal = formatDollarFractionZValueForSmallFractionDisplay(
          fractionInput,
          ruleOfEights,
        );
        displayValue = <>{quoteDisplayValue}{zValueDecimal}</>;
      } else {
        displayValue = fractionStr;
      }
    } else {
      displayValue = formatPriceWithNotation(fillPrice, notation);
    }
  } else {
    displayValue = formatPriceWithNotation(fillPrice, notation);
  }

  return (
    <div
      data-testid="fillsSummaryPriceCellContainer"
      id="fillsSummaryPriceCellContainer"
      className="justify-center text-center"
      style={{ width: "100%" }}
    >
      {displayValue}
    </div>
  );
};

export default FillsSummaryPriceCellRenderer;

/**
 * AG Grid valueFormatter for copy/paste functionality.
 * Returns the fillPrice with notation rounding applied but WITHOUT notation suffix.
 * Copy format: "4.50 " (value only, no %)
 */
export function fillsSummaryPriceValueFormatter(params: any): string {
  if (!params.data) {
    return "";
  }

  const { fullTradeData } = returnAllTradeDetailsFromGenericParams(params);
  const esid = fullTradeData?.esid;
  const blockId = params.data?.blockId;

  if (!esid || blockId === undefined) {
    return "";
  }

  const legData = getLegSummaryDataFromStore(esid, blockId, params.data);
  const fillPrice = legData.fillPrice;
  const notation = legData.notation;

  return formatPriceForDisplay(fillPrice, notation);
}
